import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess } from "@/lib/roles";
import { AlertTriangle, Plus, Search, Package, ShieldAlert } from "lucide-react";
import { SOURCE_LABEL, SEVERITY_LABEL, STATUS_LABEL } from "@/lib/damagePdf";

const SEV_CLS = {
  minor: "bg-blue-500/10 text-blue-300 border-blue-400/40",
  moderate: "bg-amber-500/10 text-amber-300 border-amber-400/40",
  major: "bg-orange-500/10 text-orange-300 border-orange-400/40",
  total_loss: "bg-red-500/10 text-red-300 border-red-400/40",
};
const STATUS_CLS = {
  reported: "bg-slate-500/10 text-slate-300 border-slate-400/40",
  under_review: "bg-purple-500/10 text-purple-300 border-purple-400/40",
  action_required: "bg-amber-500/10 text-amber-300 border-amber-400/40",
  resolved: "bg-emerald-500/10 text-emerald-300 border-emerald-400/40",
  closed: "bg-slate-500/10 text-slate-400 border-slate-500/40",
};

function humanTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export default function DamageList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canReport = hasAccess(user?.role, ["admin", "gm", "manager", "assistant_manager", "inventory_manager", "employee", "zone_owner", "cashier", "inventory_assistant"]);
  const canSeeCosts = hasAccess(user?.role, ["admin", "gm"]);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [status, setStatus] = useState("all");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (source !== "all") p.set("source_type", source);
    if (severity !== "all") p.set("severity", severity);
    if (status !== "all") p.set("status_", status);
    api.get(`/damages?${p.toString()}`).then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/damage_dashboard").then(({ data }) => setStats(data)).catch(() => {});
  }, [q, source, severity, status]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="damage-list-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Loss Prevention</div>
          <h1 className="font-heading text-3xl sm:text-4xl">Damage Log</h1>
          <div className="text-[color:var(--text-secondary)] text-sm mt-1">Central record of damaged inventory across all sources</div>
        </div>
        {canReport && (
          <Link to="/damages/new" className="hos-btn-primary flex items-center gap-2" data-testid="report-damage-btn">
            <ShieldAlert size={16}/> Report Damage
          </Link>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="hos-card p-4">
            <div className="label-caps">Total Damage Records</div>
            <div className="font-heading text-3xl mt-1 text-[color:var(--gold)]">{stats.count || 0}</div>
          </div>
          <div className="hos-card p-4">
            <div className="label-caps">Units Damaged</div>
            <div className="font-heading text-3xl mt-1">{stats.total_units || 0}</div>
          </div>
          {canSeeCosts && (
            <div className="hos-card p-4">
              <div className="label-caps">Estimated Cost</div>
              <div className="font-heading text-3xl mt-1 text-[color:var(--warning)]">${Number(stats.total_cost || 0).toFixed(2)}</div>
            </div>
          )}
          <div className="hos-card p-4">
            <div className="label-caps">Needs Review</div>
            <div className="font-heading text-3xl mt-1 text-[color:var(--warning)]">{(stats.needs_review || []).length}</div>
          </div>
        </div>
      )}

      {stats && Object.keys(stats.by_source || {}).length > 0 && (
        <div className="hos-card p-4">
          <div className="label-caps mb-2">Damage by Source</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            {Object.entries(stats.by_source).map(([k, v]) => (
              <div key={k} className="border border-[color:var(--border)] rounded-md p-3">
                <div className="text-xs text-[color:var(--text-secondary)]">{SOURCE_LABEL[k] || k}</div>
                <div className="font-heading text-2xl">{v.count}</div>
                <div className="text-xs text-[color:var(--text-secondary)]">{v.units} units{canSeeCosts && v.cost ? ` · $${v.cost.toFixed(2)}` : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="hos-card p-4 grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
        <div className="md:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-3 text-[color:var(--text-secondary)]"/>
          <input className="hos-input pl-9" placeholder="Search ref, product, SKU, supplier…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="damage-search"/>
        </div>
        <select className="hos-input" value={source} onChange={(e) => setSource(e.target.value)} data-testid="source-filter">
          <option value="all">All sources</option>
          {Object.entries(SOURCE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className="hos-input" value={severity} onChange={(e) => setSeverity(e.target.value)}>
          <option value="all">All severities</option>
          {Object.entries(SEVERITY_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select className="hos-input" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="all">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>

      <div className="hos-card overflow-hidden">
        {items.length === 0 && (
          <div className="p-10 text-center text-[color:var(--text-secondary)]">
            No damage records yet. Report one via <b>Report Damage</b>.
          </div>
        )}
        {items.length > 0 && (
          <ul className="divide-y divide-[color:var(--border)]">
            {items.map((d) => (
              <li key={d.id}>
                <Link to={`/damages/${d.id}`} className="block p-4 hover:bg-[color:var(--bg-surface-2)]/40" data-testid={`damage-row-${d.reference_number}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold">{d.reference_number} · {d.product_name || "—"}</div>
                      <div className="text-xs text-[color:var(--text-secondary)]">
                        {SOURCE_LABEL[d.source_type]}
                        {d.receiving_reference ? ` · ${d.receiving_reference}` : ""}
                        {d.display_zone_name ? ` · ${d.display_zone_name}` : ""}
                        {d.quantity ? ` · ${d.quantity} units` : ""}
                        {" · "}{humanTime(d.created_at)}
                        {" · "}{d.reported_by_name}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-2 py-1 rounded-full border ${SEV_CLS[d.severity]}`}>{SEVERITY_LABEL[d.severity]}</span>
                      <span className={`text-[10px] px-2 py-1 rounded-full border ${STATUS_CLS[d.status] || "border-[color:var(--border)]"}`}>{STATUS_LABEL[d.status]}</span>
                      {canSeeCosts && d.estimated_damage_cost != null && (
                        <span className="text-xs text-[color:var(--warning)] font-semibold">${Number(d.estimated_damage_cost).toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
